import HelpIcon from "@mui/icons-material/Help";
import BlockIcon from "@mui/icons-material/Block";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import { Panel } from "@poseidon/ui/components";
import { getDate } from "@poseidon/firebase/timestamp";

const CheckListItemIcon = ({ status }) => {
  if (status === "unknown") {
    return <HelpIcon color="warning" />;
  }
  if (status === "pendingApproval") {
    return <HelpIcon color="warning" />;
  }
  if (status === "not-applicable") {
    return <BlockIcon color="info" />;
  }
  if (status === null || status === "pending") {
    return <PendingIcon color="disabled" />;
  }
  if (status === true || status === "complete") {
    return <CheckCircleIcon color="success" />;
  }
  if (status === false || status === "actionRequired") {
    return <CancelIcon color="error" />;
  }
  return <HelpIcon />;
};
const CheckListItemRow = ({ status, label, message }) => {
  return (
    <TableRow>
      <TableCell sx={{ px: 0 }} width={32}>
        <CheckListItemIcon status={status} />
      </TableCell>
      <TableCell component="th" scope="row">
        {label}
      </TableCell>
      <TableCell>{message}</TableCell>
    </TableRow>
  );
};

function getMessageForDealerAction(document, state) {
  if (state === "pendingApproval") {
    return "Submitted, thank you - now waiting for Salt to review";
  }

  if (state === "complete") {
    return "Submitted and approved by Salt, thank you";
  }

  if (state === "actionRequired") {
    return `Please upload the relevant ${document} files below.`;
  }
  return "";
}
function getMessageForSailorAction(state) {
  if (state === "pendingApproval") {
    return "Sailor has submitted - now waiting for Salt to review";
  }

  if (state === "complete") {
    return "Sailor has submitted and it has been approved by Salt";
  }

  if (state === "actionRequired" || state === "pending") {
    return `Waiting for the sailor to provide`;
  }
  return "";
}
const checkAllApplicants = (applicants, field) => {
  return applicants.every((a) => a?.sailor[field] === true) || null;
};
export function DealerApplicationProgress({ application, boat }) {
  const boatSpecStatus = !!(
    boat.yearBuilt &&
    boat.engineType &&
    boat.engineNumber
  );
  const supportingDocsDone = checkAllApplicants(
    application.applicants,
    "supportingDocsDone"
  );
  const kycAmlDone = checkAllApplicants(
    application.applicants,
    "kycAmlChecksDone"
  );
  const idvDone = checkAllApplicants(application.applicants, "idvDone");
  return (
    <Panel title="Application Tracker">
      <Table
        size="small"
        sx={{
          ".MuiTableCell-root": {
            borderColor: "neutral.200",
          },
        }}
      >
        <TableBody>
          <CheckListItemRow
            status={true}
            label="Application Created"
            message={getDate(application.createdAt)?.toLocaleDateString() ?? ""}
          />
          <CheckListItemRow
            status={supportingDocsDone}
            label="Supporting Documentation"
            message={
              supportingDocsDone
                ? "Sailor has provided all required documents to support their credit check"
                : "Awaiting documents from sailor and/or approval from salt"
            }
          />
          <CheckListItemRow
            status={kycAmlDone}
            label="KYC/AML Checks"
            message={
              kycAmlDone
                ? "Sailor has passed their KYC & AML Checks"
                : "Awaiting checks"
            }
          />
          <CheckListItemRow
            status={idvDone}
            label="ID verified"
            message={
              idvDone
                ? "Sailor ID has been verified by Salt"
                : "Awaiting verification"
            }
          />
          <CheckListItemRow
            status={application.creditApproved ? true : null}
            label="Credit approved"
            message={`Expires ${application?.creditSummaryStatusExpiry?.toLocaleDateString()}`}
          />
          <CheckListItemRow
            status={application?.action_survey_dealerState}
            label="Survey supplied"
            message={getMessageForSailorAction(
              application?.action_survey_dealerState
            )}
          />
          <CheckListItemRow
            status={application?.action_invoices_dealerState}
            label="Invoice supplied"
            message={getMessageForDealerAction(
              "Invoice",
              application?.action_invoices_dealerState
            )}
          />
          <CheckListItemRow
            status={application?.action_insurance_dealerState}
            label="Insurance supplied"
            message={getMessageForSailorAction(
              application?.action_insurance_dealerState
            )}
          />
          <CheckListItemRow
            status={!!boat.hin}
            label="Boat HIN"
            message={
              boat.hin
                ? "Boat HIN provided, thank you"
                : "Please provide a boat HIN below"
            }
          />
          <CheckListItemRow
            status={boatSpecStatus}
            label="Boat specification"
            message={
              boatSpecStatus
                ? "Required fields complete, thank you"
                : 'Please update the fields under "Boat Specification" below'
            }
          />
          <CheckListItemRow
            status={application?.action_boatDocumentation_dealerState}
            label="Boat documentation"
            message={getMessageForDealerAction(
              "Boat Documentation",
              application?.action_boatDocumentation_dealerState
            )}
          />
          <CheckListItemRow
            status={application?.action_loanDocs_dealerState}
            label="Loan documents signed"
            message={
              application?.action_loanDocs_dealerState === "complete"
                ? "Loan documents have now been signed"
                : ""
            }
          />
          <CheckListItemRow
            status={application.completionDate ? true : null}
            label="Completion date set"
            message={
              getDate(application.completionDate)?.toLocaleDateString() ??
              "No completion date set yet"
            }
          />
        </TableBody>
      </Table>
    </Panel>
  );
}
